import React, { useState } from "react";
import { FastField } from "formik";
import map from "lodash/map";
import upperFirst from "lodash/upperFirst";
import Autocomplete from "@mui/material/Autocomplete";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import SvgIcon from "@mui/material/SvgIcon";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import ScanditScanner from "../barcode/ScanditScanner/ScanditScanner";
import useAuth from "../../hooks/useAuth";

const styles = {
  scanOption: {
    border: 1,
    borderColor: "primary.main",
    borderRadius: 1,
    color: "primary.main",
    cursor: "pointer",
    mt: 1,
    px: 2,
    py: 0.75,
  },
};

const CustomListbox = React.forwardRef(function CustomListbox(props, ref) {
  const { onScannerClick, disabled, ...other } = props;

  return (
    <Box ref={ref}>
      {!disabled && (
        <Box
          sx={styles.scanOption}
          onMouseDown={onScannerClick}
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
              onScannerClick();
            }
          }}
        >
          <SvgIcon fontSize="small" style={{ marginRight: 8 }}>
            <QrCodeScannerIcon color="primary" />
          </SvgIcon>
          Scan a Barcode
        </Box>
      )}
      <ul {...other} />
    </Box>
  );
});

const ScannerAutocomplete = ({ textFieldProps, saveCustomOption, field, form, meta, ...props }) => {
  const [open, setOpen] = useState(false);
  const [openScan, setOpenScan] = useState(false);
  const { user } = useAuth();
  const { setFieldValue, setTouched } = form;
  const options = map(props.options || [], option => ({
    ...option,
    label: option.label || "No Label",
  }));
  const error = (meta.touched || form.submitCount > 0) && meta.error;
  const helperText = error;
  const { name } = field;

  const onOptionSelected = option => {
    if (typeof option === "string") {
      const selected = options.find(v => v.value === option);
      return selected ? selected.label : option;
    }

    return option ? option.label : "";
  };

  const handleToggleScan = () => {
    setOpenScan(v => !v);
    setOpen(false);
  };

  const handleDetectedCode = code => {
    setFieldValue(name, code);
    props.onDetected && props.onDetected(code);
  };

  const getSelectedOption = () => {
    const selectedOption = options.find(option => option.value === field.value);
    if (selectedOption) {
      return selectedOption;
    }
    if (field.value) {
      return { value: field.value, label: field.value };
    }
    return null;
  };

  return (
    <>
      <Autocomplete
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        {...props}
        id={props.id ? props.id : `field-${name}`}
        data-id={`select${upperFirst(name)}`}
        onChange={(e, value) => {
          setFieldValue(name, value ? value.value : "");
          if (props.onChange) props.onChange(value);
        }}
        options={options}
        value={getSelectedOption()}
        getOptionLabel={onOptionSelected}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        freeSolo={saveCustomOption}
        onBlur={() => setTouched({ [name]: true })}
        ListboxComponent={listboxProps => (
          <CustomListbox {...listboxProps} onScannerClick={handleToggleScan} disabled={props.disabled} />
        )}
        renderInput={inputProps => (
          <TextField
            {...inputProps}
            size="small"
            name={name}
            helperText={helperText}
            error={error}
            label={props.label}
            placeholder={props.placeholder}
            variant="outlined"
            margin="none"
            {...textFieldProps}
            onBlur={
              saveCustomOption
                ? value => {
                    setFieldValue(name, value.target.value);
                  }
                : inputProps.onBlur
            }
            onKeyDown={e => {
              if (e.key === "Enter") {
                setOpen(true);
              }
            }}
          />
        )}
      />
      {openScan && (
        <ScanditScanner
          onDetected={handleDetectedCode}
          onClose={handleToggleScan}
        />
      )}
    </>
  );
};

const ScannerAutocompleteWrapper = props => {
  return (
    <FormControl fullWidth>
      <FastField {...props}>
        {({ field, form, meta }) => (
          <ScannerAutocomplete
            {...props}
            field={field}
            form={form}
            meta={meta}
            textFieldProps={{
              fullWidth: true,
              margin: "none",
              variant: "outlined",
              ...props.textFieldProps,
            }}
          />
        )}
      </FastField>
    </FormControl>
  );
};

export default ScannerAutocompleteWrapper;
